import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, X } from "lucide-react";
import { t, formatEur } from "@/i18n/sk";
import { getProducts, resolveImg } from "@/lib/api";

export function SearchModal({ open, onClose }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
      document.body.style.overflow = "hidden";
    } else {
      setQuery("");
      setResults([]);
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await getProducts({ q: query.trim() });
        setResults(data.slice(0, 6));
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query]);

  const submit = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    onClose();
    navigate(`/katalog?q=${encodeURIComponent(query.trim())}`);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center px-4 pt-24 sm:pt-32">
      <div className="absolute inset-0 bg-vault-black/80 backdrop-blur-md" onClick={onClose} />
      <div className="relative w-full max-w-2xl overflow-hidden rounded-2xl border border-white/10 bg-vault-midnight shadow-2xl">
        <form onSubmit={submit} className="flex items-center gap-3 border-b border-white/8 px-5 py-4">
          <Search className="h-5 w-5 text-cyan-300" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="search-input"
            placeholder={t.actions.searchPlaceholder}
            className="flex-1 bg-transparent text-base text-white placeholder:text-slate-500 focus:outline-none"
          />
          <button type="button" onClick={onClose} data-testid="search-close" className="text-slate-400 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </form>

        {query.trim() && (
          <div className="max-h-[60vh] overflow-y-auto p-2">
            {loading && <div className="px-4 py-6 text-center text-sm text-slate-500">Hľadám…</div>}
            {!loading && results.length === 0 && (
              <div className="px-4 py-6 text-center text-sm text-slate-500">{t.catalog.noResults}</div>
            )}
            {results.map((p) => (
              <Link
                key={p.id}
                to={`/produkt/${p.slug}`}
                onClick={onClose}
                data-testid={`search-result-${p.slug}`}
                className="flex items-center gap-4 rounded-xl px-3 py-3 transition-colors hover:bg-white/5"
              >
                <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-white/8 bg-vault-black/60 p-1.5">
                  {p.images?.[0] && <img src={resolveImg(p.images[0])} alt={p.name} className="h-full w-full object-contain" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[0.65rem] uppercase tracking-wider text-cyan-300">{p.expansion_name}</div>
                  <div className="truncate text-sm font-semibold text-white">{p.name}</div>
                </div>
                <div className="font-mono text-sm font-bold text-white">{formatEur(p.price_eur)}</div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default SearchModal;
